import { useRef, useState } from "react";
import { createPortal } from "react-dom";

/** Mała ikonka „i" z dymkiem po najechaniu — renderowana przez portal do <body>,
 * żeby nie przycinały jej kontenery z overflow (tabele, karty). Dla biznesu:
 * najeżdża i czyta, co znaczy dana kolumna / metryka. */
export function InfoDot({ text, className = "" }: { text: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: Math.max(8, r.left - 4) });
  };
  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={() => setPos(null)}
      className={`ml-1 inline-flex items-center justify-center align-middle w-[14px] h-[14px] rounded-full border text-[9px] font-bold leading-none cursor-help select-none text-[var(--color-text-muted)] border-[var(--color-text-muted)]/40 ${className}`}
      aria-label={text}
    >
      i
      {pos &&
        createPortal(
          <div
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 70, maxWidth: 340 }}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs font-normal normal-case tracking-normal text-[var(--color-text)] shadow-lg"
          >
            {text}
          </div>,
          document.body,
        )}
    </span>
  );
}

/** Chip „DLC_05" przy analizach AI Doradcy — użytkownik widzi, że tę analizę
 * robi moduł Doradca AI (DLC_05), odrębny od finansowego FIN-ARONA (DLC_06). */
export function Dlc05Badge({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-block align-middle ml-2 text-[9px] font-mono font-bold uppercase tracking-wider rounded px-1.5 py-0.5 leading-none bg-gradient-to-r from-violet-600 to-indigo-500 text-white shadow-sm ${className}`}
      title="Analizy AI Doradcy ZE — moduł DLC_05"
    >
      DLC_05
    </span>
  );
}

/** Polska etykieta na wierzchu + surowa wartość techniczna mniejsza pod spodem. */
export function Labeled({ pl, raw }: { pl: string; raw?: string | null }) {
  return (
    <span className="inline-flex flex-col leading-tight">
      <span>{pl}</span>
      {raw && raw !== pl && (
        <span className="text-[10px] font-mono text-[var(--color-text-muted)]">{raw}</span>
      )}
    </span>
  );
}

// ── Mapy enum → polski ─────────────────────────────────────────────────────────
export const SLA_SCOPE_PL: Record<string, string> = {
  branch: "Branża",
  client: "Klient",
  project: "Projekt",
  office: "Urząd",
  parcel: "Działka",
  owner: "Właściciel",
};

export const APPLIES_TO_PL: Record<string, string> = {
  milestone: "Kamień milowy",
  proceeding: "Sprawa urzędowa",
  owner_access: "Dostęp właściciela",
  parcel_status: "Status działki",
  wpw_handover: "Przekazanie WPW",
  location: "Uzgodnienie lokalizacji",
};

// Projektant i koordynator to dla biznesu TA SAMA strona („my") — nie rozróżniamy.
export const RESPONSIBLE_ROLE_PL: Record<string, string> = {
  designer: "My",
  coordinator: "My",
  office: "Urząd",
  negotiator: "Negocjator",
  investor: "Inwestor",
  director: "Dyrektor",
  ceo: "Zarząd",
};

/** Klucze kamieni (MilestoneType) — pełne polskie nazwy procesowe. */
export const MILESTONE_KEY_PL: Record<string, string> = {
  koncepcja: "Koncepcja przyłącza",
  wnioski: "Wnioski / uzgodnienia branżowe",
  mpzp_ulicp: "Plan miejscowy / ULICP",
  odrolnienie: "Odrolnienie / odlesienie",
  zabytki: "Konserwator zabytków",
  wp: "Warunki przyłączenia",
  zud: "Narada koordynacyjna (ZUD)",
  pw: "Projekt budowlany (PW)",
  mdcp: "Mapa do celów projektowych",
  foto: "Inwentaryzacja fotograficzna",
  wypis: "Wypis z rejestru gruntów",
  // Dane o sieci od ZE (numery stacji / linii / słupów) — nie mylić z mapami (MZ, MDCP).
  dane: "Dane sieciowe (ZE)",
  dane_sieciowe: "Dane sieciowe (ZE)",
  uzgodnienie_lokalizacji: "Uzgodnienie lokalizacji",
};

/** Klucze SLA per applies_to (poza kamieniami). */
export const SLA_KEY_PL: Record<string, string> = {
  "*": "wszystkie (domyślnie)",
  action_required: "Wymaga działania",
  notification: "Zawiadomienie",
  office: "Sprawa w urzędzie",
  owner: "Zgoda właściciela",
  technical: "Uzgodnienie techniczne",
  // zegary lokalizacji: kategorie działek + rodzaje elementów toru
  jst: "JST",
  private_connected: "Prywatna — przyłączana",
  private_transit: "Prywatna — tranzyt",
  gddkia: "GDDKiA",
  pkp: "PKP i spółki kolejowe",
  kowr: "KOWR",
  lasy: "Lasy Państwowe",
  wody: "Wody Polskie",
  other: "Inne",
  art124: "Art. 124",
  uzupelnienie: "Uzupełnienie",
  odmowa_urzedu: "Odmowa urzędu",
  odwolanie: "Odwołanie",
  ponowny_wniosek: "Ponowny wniosek",
  zgloszenie_ze: "Zgłoszenie do ZE",
  przeprojektowanie: "Przeprojektowanie",
};

/** Polska etykieta klucza SLA zależnie od kontekstu (applies_to). */
export function slaKeyPl(appliesTo: string, key: string): string {
  if (!key || key === "*") return "wszystkie (domyślnie)";
  if (appliesTo === "milestone") return MILESTONE_KEY_PL[key] ?? key;
  return SLA_KEY_PL[key] ?? key;
}

// ── Opisy metryk / kolumn dla dymków „i" ───────────────────────────────────────
export const HELP = {
  slaScope: "Poziom, na którym obowiązuje reguła. Im głębszy poziom, tym ma pierwszeństwo: Branża < Klient < Projekt < Urząd/Działka < Właściciel.",
  slaApplies: "Do czego stosuje się termin: kamień milowy, sprawa urzędowa, dostęp właściciela, status działki albo przekazanie WPW.",
  slaKey: "Konkretny rodzaj w ramach typu (np. który kamień, jaki rodzaj sprawy). „wszystkie” = reguła zbiorcza dla całego typu.",
  slaDays: "Termin (SLA) w dniach — po ilu dniach bez ruchu pozycja jest przekroczona.",
  slaWarn: "Ile dni przed przekroczeniem terminu pozycja zapala się na żółto (ostrzeżenie).",
  slaRole: "Kto domyślnie odpowiada za tę pozycję (czyja „piłka” po naszej stronie).",
  daysInCourt: "Dni w OBECNYM stanie, liczone po stronie aktualnego posiadacza piłki (nasze lub urzędu — patrz kolumna „Piłka”). To NIE jest suma łączna od początku sprawy ani osobny zegar nasz/urzędu — to czas na obecnej stronie od wejścia w ten stan.",
  daysIdle: "Ile dni bez jakiegokolwiek ruchu na pozycji (bezczynność).",
  whoseCourt: "Po czyjej stronie jest teraz „piłka” — i czyje SLA jest liczone: nasze (do zrobienia u nas) albo urzędu/właściciela (czekamy na odpowiedź). Zegar SLA biegnie tej stronie, która trzyma piłkę.",
  breached: "Liczba pozycji z przekroczonym terminem SLA (czerwone). Rozbicie „nasze / urzędu” mówi, po której stronie jest przekroczenie (nasze = nasza zwłoka; urzędu = przekroczony termin ustawowy urzędu).",
  blockedSince: "Od kiedy projekt nieprzerwanie ma ≥1 bloker (przekroczony SLA) — czyli kiedy OSTATNI RAZ wszystkie blokery były rozwiązane. „brak” = projekt jest teraz wolny od blokerów.",
  warn: "Liczba pozycji zbliżających się do terminu (żółte).",
  stalled: "Pozycje bez ruchu dłużej niż próg bezczynności — grożą zapomnieniem.",
  dormancy: "Ile dni całe przedsięwzięcie nie miało żadnego zdarzenia (uśpienie projektu).",
  completion: "Prognozowana data domknięcia projektu przy obecnej ścieżce krytycznej.",
  backlogAge: "Rozkład otwartych sugestii wg wieku — jak długo czekają nieobsłużone.",
  throughput: "Ile sugestii w okresie zgłoszono, zrealizowano (DONE) i odrzucono (DISMISSED) — osobno, żeby odrzucanie nie udawało realizacji.",
  resolveTime: "Średni i medianowy czas od zgłoszenia rady do jej domknięcia (mediana odporniejsza na skrajne przypadki).",
  verifiedRate: "Odsetek rad oznaczonych jako „zrobione”, które faktycznie zniknęły ze stanu projektu (realnie domknięte, nie tylko odklikane).",
  reopened: "Ile rad wróciło po oznaczeniu jako zrobione (sygnał pojawił się ponownie).",
  untouched: "Projekty z sugestiami, których nikt nie ruszył od co najmniej N dni — ryzyko dryfu.",
} as const;
